/**
 * Further & Further - Hero Reveal Slider
 * 
 * This script handles:
 * 1. Initializing the Slick slider for the hero section
 * 2. Creating the "reveal" effect where the cursor reveals the hidden text behind the video
 */

class HeroRevealSlider {
  constructor(element) {
    this.slider = element;
    this.sliderId = this.slider.dataset.sliderId;
    this.slides = this.slider.querySelectorAll('.hero-slide');
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Get settings from the section if available
    const sectionSettings = document.querySelector(`#shopify-section-${this.sliderId}`);
    this.clipRadius = parseInt(sectionSettings?.dataset?.revealRadius || 200); // Size of the reveal circle in pixels
    
    // Hotspot detection settings
    this.hotspotRadius = 162.5; // Half of 325px (hotspot width/height)
    this.hotspotBuffer = 30; // Buffer size when hovering over hotspot (30px bigger than hotspot)
    // Subtle shrink effect - slightly smaller than normal but not too dramatic
    this.shrunkRadius = 160; // Shrinks from 200px to 150px for a noticeable but subtle effect
    this.currentRadius = this.clipRadius; // Track current radius for smooth transitions
    this.targetRadius = this.clipRadius; // Target radius for animation
    this.radiusTransitionSpeed = 0.25; // Speed of radius transition (0-1, higher = faster)
    
    // Animation frame tracking for performance
    this.animationFrameId = null;
    this.lastCursorX = 0;
    this.lastCursorY = 0;
    
    this.init();
  }

  init() {
    // Initialize Slick slider
    this.initSlider();
    
    // Setup reveal effect for each slide
    this.slides.forEach(slide => {
      this.setupRevealEffect(slide);
    });
    
    // Setup hash-based smooth scrolling for button links
    this.initHashNavigation();
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  initSlider() {
    // Ensure jQuery and Slick are available
    if (typeof $ === 'undefined' || typeof $.fn.slick === 'undefined') {
      console.warn('jQuery or Slick not available, retrying in 100ms');
      setTimeout(() => this.initSlider(), 100);
      return;
    }

    // Check if slider already initialized
    if ($(this.slider).hasClass('slick-initialized')) {
      return;
    }

    // Ensure slides exist
    if (this.slides.length === 0) {
      console.warn('No slides found for slider initialization');
      return;
    }

    try {
      // Initialize Slick with updated options for version 1.8.1
      $(this.slider).slick({
        dots: this.slides.length > 1,
        arrows: false,
        infinite: this.slides.length > 1,
        speed: 500,
        fade: true,
        cssEase: 'linear',
        autoplay: this.slides.length > 1,
        autoplaySpeed: 10000,
        pauseOnHover: true,
        adaptiveHeight: false,
        lazyLoad: 'ondemand',
        accessibility: false,
        focusOnSelect: false,
        swipe: this.slides.length > 1,
        touchMove: this.slides.length > 1
      });
    } catch (error) {
      console.error('Error initializing Slick slider:', error);
      return;
    }

    // Additional setup after slick is initialized
    $(this.slider).on('afterChange', (event, slick, currentSlide) => {
      // Get the current active slide and reset cursor position
      const activeSlide = this.slides[currentSlide];
      if (activeSlide) {
        // Reset radius values when changing slides
        this.currentRadius = this.clipRadius;
        this.targetRadius = this.clipRadius;
        
        // Reset clip path to hide reveal content
        if (activeSlide.clipElement) {
          activeSlide.clipElement.style.clipPath = 'circle(0px at 50% 50%)';
        }
        // Reset inverted containers clip path
        if (activeSlide.invertedContainer) {
          activeSlide.invertedContainer.style.clipPath = 'circle(0px at 50% 50%)';
        }
        if (activeSlide.buttonInvertedContainer) {
          activeSlide.buttonInvertedContainer.style.clipPath = 'circle(0px at 50% 50%)';
        }
        if (activeSlide.textRevealContainer) {
          activeSlide.textRevealContainer.style.clipPath = 'circle(0px at 50% 50%)';
        }
        // Remove reveal-active class from all slides
        this.slides.forEach(slide => slide.classList.remove('reveal-active'));
        // Reposition inverted buttons for the active slide
        this.positionInvertedButtons(activeSlide);
      }
    });
    
    // Position inverted buttons after slider initialization
    setTimeout(() => {
      this.slides.forEach(slide => this.positionInvertedButtons(slide));
    }, 100);
  }

  setupRevealEffect(slide) {
    // Get the reveal text element that contains the hotspots and text
    const revealTextElement = slide.querySelector('.hero-slide__reveal-text');
    const invertedContainer = slide.querySelector('.hero-slide__ripped-inverted-container');
    const buttonInvertedContainer = slide.querySelector('.hero-slide__button-inverted-container');
    const textRevealContainer = slide.querySelector('.hero-slide__text-reveal-container');
    
    if (!revealTextElement) {
      console.warn('No reveal text element found for slide');
      return;
    }
    
    // Store references to the reveal elements on the slide
    slide.clipElement = revealTextElement;
    slide.invertedContainer = invertedContainer;
    slide.buttonInvertedContainer = buttonInvertedContainer;
    slide.textRevealContainer = textRevealContainer;
    
    // Set initial clip state to hide the reveal content
    revealTextElement.style.clipPath = 'circle(0px at 50% 50%)';
    revealTextElement.style.zIndex = '2'; // Put it above the video but below content
    revealTextElement.style.pointerEvents = 'none';
    
    // Set initial clip state for inverted containers
    if (invertedContainer) {
      invertedContainer.style.clipPath = 'circle(0px at 50% 50%)';
    }
    if (buttonInvertedContainer) {
      buttonInvertedContainer.style.clipPath = 'circle(0px at 50% 50%)';
    }
    if (textRevealContainer) {
      textRevealContainer.style.clipPath = 'circle(0px at 50% 50%)';
    }
    
    // Set inverted ripped images to full opacity (no hover needed)
    this.setupRippedImageHovers(slide);
    
    // No longer setting up button hovers - inverted buttons show automatically
    
    // Position inverted buttons to match original buttons
    this.positionInvertedButtons(slide);
    
    // Enable pointer events for hotspots in the reveal layer
    this.setupHotspotInteractivity(slide);
    
    // Add event listeners for mouse movement/touch
    if (!this.isTouch) {
      // Desktop - mouse events
      slide.addEventListener('mousemove', this.handleMouseMove.bind(this, slide));
      slide.addEventListener('mouseleave', this.handleMouseLeave.bind(this, slide));
    } else {
      // Mobile - touch events
      slide.addEventListener('touchmove', this.handleTouchMove.bind(this, slide));
      slide.addEventListener('touchend', this.handleTouchEnd.bind(this, slide));
    }
  }

  setupRippedImageHovers(slide) {
    // Ripped image hover functionality removed - inverted images now show automatically through clip-path
    // The clip-path on the inverted container controls visibility as the reveal circle moves
    // No hover interaction needed - the inverted images are always at full opacity
    
    // Set all inverted images to full opacity
    const invertedImages = slide.querySelectorAll('.hero-slide__ripped-image-inverted');
    invertedImages.forEach(invertedImage => {
      invertedImage.style.opacity = '1';
    });
  }

  setupButtonHovers(slide) {
    // Button hover functionality removed - inverted buttons now show automatically through clip-path
    // The clip-path on the button inverted container controls visibility as the reveal circle moves
  }

  setupHotspotInteractivity(slide) {
    // Get all hotspots in the slide
    const hotspots = slide.querySelectorAll('.hero-slide__hotspot');
    
    hotspots.forEach(hotspot => {
      // Enable pointer events for the hotspot
      hotspot.style.pointerEvents = 'auto';
      
      // Ensure videos within hotspots remain interactive
      const videos = hotspot.querySelectorAll('.hero-slide__hotspot-video');
      videos.forEach(video => {
        video.style.pointerEvents = 'auto';
      });
      
      // Add hover event listeners for better control
      hotspot.addEventListener('mouseenter', () => {
        // Ensure hover effects are applied
        hotspot.classList.add('hotspot-hover');
      });
      
      hotspot.addEventListener('mouseleave', () => {
        // Remove hover class
        hotspot.classList.remove('hotspot-hover');
      });
    });
  }

  initHashNavigation() {
    // Set up smooth scrolling for buttons with hash links
    this.slides.forEach(slide => {
      // Find both regular and inverted buttons with hash links
      const buttons = [
        ...slide.querySelectorAll('.hero-slide__button[href^="#"]'),
        ...slide.querySelectorAll('.hero-slide__button-inverted[href^="#"]')
      ];
      
      buttons.forEach(button => {
        button.addEventListener('click', (e) => {
          const hash = button.getAttribute('href');
          
          // Validate hash format
          if (!hash || hash === '#') return;
          
          // Find target element
          const targetElement = document.querySelector(hash);
          
          if (targetElement) {
            // Prevent default navigation
            e.preventDefault();
            
            // Smooth scroll to target element
            window.scrollTo({
              top: targetElement.offsetTop,
              behavior: 'smooth'
            });
          }
          // If target doesn't exist, allow normal link behavior
        });
      });
    });
  }

  positionInvertedButtons(slide) {
    const button = slide.querySelector('.hero-slide__button');
    const buttonInvertedContainer = slide.querySelector('.hero-slide__button-inverted-container');
    
    if (!button || !buttonInvertedContainer) return;
    
    const invertedButton = buttonInvertedContainer.querySelector('.hero-slide__button-inverted');
    if (!invertedButton) return;
    
    // Get button position relative to the slide
    const slideRect = slide.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    
    // Calculate position relative to slide
    const buttonLeft = buttonRect.left - slideRect.left;
    const buttonTop = buttonRect.top - slideRect.top;
    
    // Apply positioning to inverted button
    invertedButton.style.position = 'absolute';
    invertedButton.style.left = buttonLeft + 'px';
    invertedButton.style.top = buttonTop + 'px';
    invertedButton.style.transform = 'scale(1.02)';
    // Ensure button is always visible (visibility controlled by clip-path)
    invertedButton.style.opacity = '1';
    
    // Update on window resize
    if (!this.buttonResizeHandler) {
      this.buttonResizeHandler = () => {
        this.slides.forEach(s => this.positionInvertedButtons(s));
      };
      window.addEventListener('resize', this.buttonResizeHandler);
    }
  }

  detectHotspotProximity(slide, cursorX, cursorY) {
    // Get all hotspots in the current slide
    const hotspots = slide.querySelectorAll('.hero-slide__hotspot');
    
    let isNearHotspot = false;
    let minDistance = Infinity;
    
    hotspots.forEach(hotspot => {
      // Get hotspot position relative to slide
      const hotspotRect = hotspot.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      
      // Calculate hotspot center position relative to slide
      const hotspotCenterX = (hotspotRect.left - slideRect.left) + (hotspotRect.width / 2);
      const hotspotCenterY = (hotspotRect.top - slideRect.top) + (hotspotRect.height / 2);
      
      // Calculate distance between cursor and hotspot center
      const distance = Math.sqrt(
        Math.pow(cursorX - hotspotCenterX, 2) + 
        Math.pow(cursorY - hotspotCenterY, 2)
      );
      
      // Track minimum distance for smoother transitions
      if (distance < minDistance) {
        minDistance = distance;
      }
      
      // Check if cursor is within or near the hotspot
      // We use a slightly larger detection radius for smoother transitions
      const detectionRadius = this.hotspotRadius + 50; // Add 50px buffer for detection
      if (distance <= detectionRadius) {
        isNearHotspot = true;
      }
    });
    
    return { isNearHotspot, minDistance };
  }

  updateRevealRadius(isNearHotspot, minDistance) {
    // Determine target radius based on hotspot proximity
    if (isNearHotspot) {
      // Calculate smooth transition based on distance
      // When very close to hotspot center, use shrunk radius
      // When at edge of detection zone, use normal radius
      const detectionRadius = this.hotspotRadius + 50;
      const proximityFactor = Math.max(0, Math.min(1, (detectionRadius - minDistance) / detectionRadius));
      
      // Interpolate between normal and shrunk radius based on proximity
      // When proximityFactor is 1 (very close), use shrunkRadius
      // When proximityFactor is 0 (at edge), use normal clipRadius
      this.targetRadius = this.clipRadius - ((this.clipRadius - this.shrunkRadius) * proximityFactor);
    } else {
      this.targetRadius = this.clipRadius;
    }
    
    // Smoothly animate current radius towards target radius
    this.currentRadius += (this.targetRadius - this.currentRadius) * this.radiusTransitionSpeed;
    
    // Return the animated radius value
    return this.currentRadius;
  }

  animateReveal(slide) {
    // Detect hotspot proximity
    const { isNearHotspot, minDistance } = this.detectHotspotProximity(slide, this.lastCursorX, this.lastCursorY);
    
    // Update the reveal radius based on hotspot proximity
    const dynamicRadius = this.updateRevealRadius(isNearHotspot, minDistance);
    
    // Debug log to verify shrink effect
    if (isNearHotspot && Math.abs(this.currentRadius - this.shrunkRadius) < 10) {
      console.log('Reveal shrunk to:', dynamicRadius, 'px (target:', this.shrunkRadius, 'px)');
    }
    
    // Update clip path position for reveal text and inverted containers with dynamic radius
    const clipPath = `circle(${dynamicRadius}px at ${this.lastCursorX}px ${this.lastCursorY}px)`;
    slide.clipElement.style.clipPath = clipPath;
    
    // Synchronize clip path with inverted containers
    if (slide.invertedContainer) {
      slide.invertedContainer.style.clipPath = clipPath;
    }
    if (slide.buttonInvertedContainer) {
      slide.buttonInvertedContainer.style.clipPath = clipPath;
    }
    if (slide.textRevealContainer) {
      slide.textRevealContainer.style.clipPath = clipPath;
    }
    
    // Continue animation if reveal is active
    if (slide.classList.contains('reveal-active')) {
      this.animationFrameId = requestAnimationFrame(() => this.animateReveal(slide));
    }
  }

  handleMouseMove(slide, event) {
    if (!slide.clipElement) return;
    
    const rect = slide.getBoundingClientRect();
    this.lastCursorX = event.clientX - rect.left;
    this.lastCursorY = event.clientY - rect.top;
    
    // Add class to indicate reveal is active
    const wasActive = slide.classList.contains('reveal-active');
    slide.classList.add('reveal-active');
    
    // Start animation loop if not already running
    if (!wasActive && !this.animationFrameId) {
      this.animateReveal(slide);
    }
  }

  handleMouseLeave(slide) {
    if (!slide.clipElement) return;
    
    // Cancel animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Reset radius values when mouse leaves
    this.currentRadius = this.clipRadius;
    this.targetRadius = this.clipRadius;
    
    // Hide the reveal content when mouse leaves
    const hiddenClipPath = 'circle(0px at 50% 50%)';
    slide.clipElement.style.clipPath = hiddenClipPath;
    
    // Synchronize clip path with inverted containers
    if (slide.invertedContainer) {
      slide.invertedContainer.style.clipPath = hiddenClipPath;
    }
    if (slide.buttonInvertedContainer) {
      slide.buttonInvertedContainer.style.clipPath = hiddenClipPath;
    }
    if (slide.textRevealContainer) {
      slide.textRevealContainer.style.clipPath = hiddenClipPath;
    }
    
    // Remove class to indicate reveal is not active
    slide.classList.remove('reveal-active');
  }

  handleTouchMove(slide, event) {
    if (!slide.clipElement || !event.touches[0]) return;
    
    const touch = event.touches[0];
    const rect = slide.getBoundingClientRect();
    this.lastCursorX = touch.clientX - rect.left;
    this.lastCursorY = touch.clientY - rect.top;
    
    // Add class to indicate reveal is active
    const wasActive = slide.classList.contains('reveal-active');
    slide.classList.add('reveal-active');
    
    // Start animation loop if not already running
    if (!wasActive && !this.animationFrameId) {
      this.animateReveal(slide);
    }
    
    // Prevent default to avoid scrolling while revealing
    event.preventDefault();
  }

  handleTouchEnd(slide) {
    if (!slide.clipElement) return;
    
    // Cancel animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Reset radius values when touch ends
    this.currentRadius = this.clipRadius;
    this.targetRadius = this.clipRadius;
    
    // Hide the reveal content when touch ends
    const hiddenClipPath = 'circle(0px at 50% 50%)';
    slide.clipElement.style.clipPath = hiddenClipPath;
    
    // Synchronize clip path with inverted containers
    if (slide.invertedContainer) {
      slide.invertedContainer.style.clipPath = hiddenClipPath;
    }
    if (slide.buttonInvertedContainer) {
      slide.buttonInvertedContainer.style.clipPath = hiddenClipPath;
    }
    if (slide.textRevealContainer) {
      slide.textRevealContainer.style.clipPath = hiddenClipPath;
    }
    
    // Remove class to indicate reveal is not active
    slide.classList.remove('reveal-active');
  }

  handleResize() {
    // Re-initialize the slider on window resize for better responsiveness
    if (typeof $ !== 'undefined' && $(this.slider).hasClass('slick-initialized')) {
      $(this.slider).slick('setPosition');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Find all hero reveal sliders on the page
  const sliders = document.querySelectorAll('.hero-reveal-slider');
  
  sliders.forEach(slider => {
    // Create a new instance for each slider
    new HeroRevealSlider(slider);
  });
});

// Handle dynamic section loading in theme editor
document.addEventListener('shopify:section:load', event => {
  const slider = event.target.querySelector('.hero-reveal-slider');
  if (slider) {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      new HeroRevealSlider(slider);
    }, 100);
  }
});

// Handle section unload in theme editor
document.addEventListener('shopify:section:unload', event => {
  const slider = event.target.querySelector('.hero-reveal-slider');
  if (slider && $(slider).hasClass('slick-initialized')) {
    $(slider).slick('unslick');
  }
});

// Hero Reveal Slider Mute Controller
class HeroRevealMuteController {
  constructor(sliderElement) {
    this.slider = sliderElement;
    this.muteButtons = sliderElement.querySelectorAll('.hero-slide-mute-button');
    this.isMuted = true; // Start muted for better UX
    
    // Track all videos in the slider
    this.backgroundVideos = sliderElement.querySelectorAll('.hero-slide__video');
    this.revealVideos = sliderElement.querySelectorAll('.hero-slide__reveal-video video');
    this.hotspotVideos = sliderElement.querySelectorAll('.hero-slide__hotspot-video');
    
    this.init();
  }
  
  init() {
    console.log('Hero Reveal Mute Controller initialized');
    console.log('Found videos:', {
      background: this.backgroundVideos.length,
      reveal: this.revealVideos.length,
      hotspot: this.hotspotVideos.length
    });
    
    // Set initial button states
    this.updateAllButtonStates();
    
    // Initially mute all slides
    const allSlides = this.slider.querySelectorAll('.hero-slide');
    allSlides.forEach(slide => {
      this.muteSlideVideos(slide);
    });
    
    // Apply initial mute state to current slide only
    const activeSlide = this.getCurrentSlide();
    if (activeSlide) {
      this.applyMuteStateToSlide(activeSlide);
    }
    
    // Add click handlers to all mute buttons
    this.muteButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMute();
      });
    });
    
    // Listen for slide changes to apply mute state to new active slide
    if (typeof $ !== 'undefined') {
      $(this.slider).on('afterChange', (event, slick, currentSlide) => {
        // Mute all slides first
        const allSlides = this.slider.querySelectorAll('.hero-slide');
        allSlides.forEach(slide => {
          this.muteSlideVideos(slide);
        });
        
        // Apply saved mute state to the newly active slide
        const activeSlide = this.getCurrentSlide();
        if (activeSlide) {
          this.applyMuteStateToSlide(activeSlide);
        }
      });
    }
    
    // Load saved user preference
    this.loadPreference();
  }
  
  getCurrentSlide() {
    // Get the current active slide using Slick's API or fallback to checking active class
    if (typeof $ !== 'undefined' && $(this.slider).hasClass('slick-initialized')) {
      const currentIndex = $(this.slider).slick('slickCurrentSlide');
      const slides = this.slider.querySelectorAll('.hero-slide');
      return slides[currentIndex] || null;
    }
    
    // Fallback: find slide with slick-active class
    return this.slider.querySelector('.hero-slide.slick-active') || this.slider.querySelector('.hero-slide');
  }
  
  toggleMute() {
    this.isMuted = !this.isMuted;
    console.log('Hero slider mute toggled:', this.isMuted ? 'MUTED' : 'UNMUTED');
    
    this.updateAllButtonStates();
    
    // Only apply mute state to the current active slide
    const activeSlide = this.getCurrentSlide();
    if (activeSlide) {
      this.applyMuteStateToSlide(activeSlide);
    }
    
    this.savePreference();
  }
  
  updateAllButtonStates() {
    this.muteButtons.forEach(button => {
      if (this.isMuted) {
        button.classList.remove('unmuted');
        button.setAttribute('aria-label', 'Unmute videos');
        button.setAttribute('title', 'Unmute videos');
      } else {
        button.classList.add('unmuted');
        button.setAttribute('aria-label', 'Mute videos');
        button.setAttribute('title', 'Mute videos');
      }
    });
  }
  
  muteSlideVideos(slide) {
    // Force mute all videos in a slide
    const backgroundVideo = slide.querySelector('.hero-slide__video');
    const revealVideo = slide.querySelector('.hero-slide__reveal-video video');
    const hotspotVideos = slide.querySelectorAll('.hero-slide__hotspot-video');
    
    if (backgroundVideo) {
      backgroundVideo.muted = true;
    }
    
    if (revealVideo) {
      revealVideo.muted = true;
    }
    
    hotspotVideos.forEach(video => {
      video.muted = true;
    });
  }
  
  applyMuteStateToSlide(slide) {
    // Find videos within the specific slide
    const backgroundVideo = slide.querySelector('.hero-slide__video');
    const revealVideo = slide.querySelector('.hero-slide__reveal-video video');
    const hotspotVideos = slide.querySelectorAll('.hero-slide__hotspot-video');
    
    let videoCount = 0;
    
    // Apply mute state to background video
    if (backgroundVideo) {
      backgroundVideo.muted = this.isMuted;
      videoCount++;
    }
    
    // Apply mute state to reveal video
    if (revealVideo) {
      revealVideo.muted = this.isMuted;
      videoCount++;
    }
    
    // Apply mute state to hotspot videos
    hotspotVideos.forEach(video => {
      video.muted = this.isMuted;
      videoCount++;
    });
    
    console.log(`Applied mute state (${this.isMuted ? 'MUTED' : 'UNMUTED'}) to ${videoCount} videos in current slide`);
  }
  
  applyMuteStateToAllVideos() {
    // Apply mute state to all slides (used for initial load)
    const slides = this.slider.querySelectorAll('.hero-slide');
    slides.forEach(slide => {
      this.applyMuteStateToSlide(slide);
    });
  }
  
  // Handle when new slides are added (if using dynamic content)
  refreshVideoList() {
    this.backgroundVideos = this.slider.querySelectorAll('.hero-slide__video');
    this.revealVideos = this.slider.querySelectorAll('.hero-slide__reveal-video video');
    this.hotspotVideos = this.slider.querySelectorAll('.hero-slide__hotspot-video');
    this.muteButtons = this.slider.querySelectorAll('.hero-slide-mute-button');
    
    this.applyMuteStateToAllVideos();
    this.updateAllButtonStates();
  }
  
  savePreference() {
    localStorage.setItem('heroRevealSliderMuted', this.isMuted);
  }
  
  loadPreference() {
    const saved = localStorage.getItem('heroRevealSliderMuted');
    if (saved !== null) {
      this.isMuted = saved === 'true';
      this.updateAllButtonStates();
      
      // First mute all slides
      const allSlides = this.slider.querySelectorAll('.hero-slide');
      allSlides.forEach(slide => {
        this.muteSlideVideos(slide);
      });
      
      // Then apply saved preference to current slide only
      const activeSlide = this.getCurrentSlide();
      if (activeSlide) {
        this.applyMuteStateToSlide(activeSlide);
      }
    }
  }
}

// Initialize mute controllers for all hero reveal sliders
function initHeroRevealMuteControllers() {
  const sliders = document.querySelectorAll('.hero-reveal-slider');
  
  sliders.forEach(slider => {
    if (!slider.dataset.muteControllerInitialized) {
      slider.dataset.muteControllerInitialized = 'true';
      new HeroRevealMuteController(slider);
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroRevealMuteControllers);
} else {
  initHeroRevealMuteControllers();
}

// Shopify theme editor support
document.addEventListener('shopify:section:load', function(event) {
  const slider = event.target.querySelector('.hero-reveal-slider');
  if (slider && !slider.dataset.muteControllerInitialized) {
    slider.dataset.muteControllerInitialized = 'true';
    new HeroRevealMuteController(slider);
  }
});